import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Mail, Phone, Trash2, Pencil, Globe, PackageSearch } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", contact_person: "", contact_email: "", phone: "", website: "", notes: "" };
// Reihenfolge der Formularfelder — bewusst Telefon → E-Mail → Website
// zusammen gruppiert (Kontaktweg-Cluster).
const FIELDS = [
  ["name", "Name *", "text"],
  ["contact_person", "Ansprechpartner", "text"],
  ["phone", "Telefon", "text"],
  ["contact_email", "E-Mail", "email"],
  ["website", "Website", "url"],
  ["notes", "Notizen", "text"],
];

/** Baut einen sicheren externen Link aus dem Website-Freitext.
 *  Nur eine minimale Absicherung — der Backend-Repository normalisiert
 *  bereits beim Speichern auf ``https://…``. */
export function safeSupplierHref(raw) {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  return "https://" + v.replace(/^\/+/, "");
}

export default function Lieferanten() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const suppliers = useQuery({
    queryKey: ["suppliers"],
    queryFn: async () => (await api.get("/suppliers")).data,
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form };
      // Leere Strings → null, damit Pydantic-Validierung nicht auf
      // "" statt None fällt (z. B. EmailStr-Feld akzeptiert "" nicht).
      for (const k of ["contact_person", "contact_email", "phone", "website", "notes"]) {
        if (payload[k] === "") payload[k] = null;
      }
      if (editingId) return (await api.put(`/suppliers/${editingId}`, payload)).data;
      return (await api.post("/suppliers", payload)).data;
    },
    onSuccess: () => {
      toast.success(editingId ? "Lieferant aktualisiert" : "Lieferant angelegt");
      setOpen(false); setForm(EMPTY); setEditingId(null);
      qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/suppliers/${id}`)).data,
    onSuccess: (data) => {
      toast.success(data?.archived ? "Lieferant archiviert (in Verwendung)" : "Lieferant gelöscht");
      qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const openNew = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name || "",
      contact_person: s.contact_person || "",
      contact_email: s.contact_email || "",
      phone: s.phone || "",
      website: s.website || "",
      notes: s.notes || "",
    });
    setOpen(true);
  };
  const confirmDelete = (s) => {
    if (window.confirm(`Lieferant "${s.name}" löschen? Wenn Produkte diesen Lieferanten referenzieren, wird er archiviert.`)) {
      del.mutate(s.id);
    }
  };
  const showProducts = (s) => {
    navigate(`/produkte?supplier=${encodeURIComponent(s.id)}`);
  };

  const linkClass = "text-cyan-400 hover:text-cyan-300 underline-offset-4 hover:underline";
  const monoLinkClass = "text-cyan-400 hover:text-cyan-300 font-mono underline-offset-4 hover:underline";

  return (
    <div data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-cyan-400">Beschaffung</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight truncate">Lieferanten</h1>
        </div>
        <Button data-testid="new-supplier-btn" onClick={openNew}
          className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-sm uppercase tracking-wider font-semibold">
          <Plus size={16} className="mr-2" /> Neuer Lieferant
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">
              {editingId ? "Lieferant bearbeiten" : "Neuer Lieferant"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {FIELDS.map(([k, l, type]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">{l}</Label>
                <Input
                  data-testid={`supplier-field-${k}`}
                  type={type}
                  placeholder={k === "website" ? "z. B. lekkerland.de" : undefined}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm w-full"
                />
                {k === "website" && (
                  <div className="text-[11px] text-zinc-500 mt-1">
                    Optional. „https://" wird beim Speichern automatisch ergänzt, falls fehlt.
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="supplier-save-btn" onClick={() => save.mutate()} disabled={!form.name}
              className="bg-cyan-400 text-zinc-950 hover:bg-cyan-300 rounded-sm">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2" data-testid="suppliers-mobile-list">
        {(suppliers.data || []).map((s) => {
          const webHref = safeSupplierHref(s.website);
          return (
          <div key={s.id} data-testid={`supplier-m-${s.id}`} className="border border-zinc-800 bg-zinc-900 rounded-sm p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{s.name}</div>
                {s.contact_person && <div className="text-xs text-zinc-400 truncate">{s.contact_person}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="icon" variant="ghost" data-testid={`supplier-products-m-${s.id}`}
                  className="text-cyan-400" title="Produkte anzeigen" onClick={() => showProducts(s)}>
                  <PackageSearch size={14} />
                </Button>
                <Button size="icon" variant="ghost" data-testid={`supplier-edit-m-${s.id}`} onClick={() => openEdit(s)}>
                  <Pencil size={14} />
                </Button>
                <Button size="icon" variant="ghost" data-testid={`supplier-delete-m-${s.id}`}
                  className="text-rose-400" onClick={() => confirmDelete(s)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <div className="mt-2 flex flex-col gap-1 text-sm">
              {s.phone ? (
                <a href={`tel:${s.phone.replace(/[^\d+]/g, "")}`} data-testid={`supplier-tel-m-${s.id}`}
                  className={`${monoLinkClass} inline-flex items-center gap-2`}>
                  <Phone size={14} className="shrink-0" /> {s.phone}
                </a>
              ) : null}
              {s.contact_email ? (
                <a href={`mailto:${s.contact_email}`} data-testid={`supplier-mail-m-${s.id}`}
                  className={`${linkClass} inline-flex items-center gap-2 truncate break-anywhere`}>
                  <Mail size={14} className="shrink-0" /> {s.contact_email}
                </a>
              ) : null}
              {webHref ? (
                <a href={webHref} target="_blank" rel="noopener noreferrer"
                  data-testid={`supplier-web-m-${s.id}`}
                  title={s.website}
                  className="inline-flex items-center gap-1.5 h-8 px-2.5 self-start rounded-sm border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 uppercase tracking-widest text-[11px] font-semibold">
                  <Globe size={12} className="shrink-0" /> Web
                </a>
              ) : null}
              {s.notes && <div className="text-xs text-zinc-500 mt-1 break-anywhere">{s.notes}</div>}
            </div>
          </div>
        );})}
        {!suppliers.data?.length && (
          <div className="border border-zinc-800 rounded-sm p-6 text-center text-zinc-500 text-sm">Noch keine Lieferanten.</div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block border border-zinc-800 bg-zinc-900 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Ansprechpartner</th>
              <th className="text-left px-4 py-3">Telefon</th>
              <th className="text-left px-4 py-3">E-Mail</th>
              <th className="text-left px-4 py-3">Website</th>
              <th className="text-left px-4 py-3">Notizen</th>
              <th className="text-right px-4 py-3">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800" data-testid="suppliers-table-body">
            {(suppliers.data || []).map((s) => {
              const webHref = safeSupplierHref(s.website);
              return (
              <tr key={s.id} data-testid={`supplier-row-${s.id}`} className="hover:bg-zinc-800/60">
                <td className="px-4 py-3">{s.name}</td>
                <td className="px-4 py-3 text-zinc-300">{s.contact_person || "—"}</td>
                <td className="px-4 py-3">
                  {s.phone ? (
                    <a href={`tel:${s.phone.replace(/[^\d+]/g, "")}`} data-testid={`supplier-tel-${s.id}`} className={monoLinkClass}>
                      {s.phone}
                    </a>
                  ) : <span className="text-zinc-500">—</span>}
                </td>
                <td className="px-4 py-3">
                  {s.contact_email ? (
                    <a href={`mailto:${s.contact_email}`} data-testid={`supplier-mail-${s.id}`} className={linkClass}>
                      {s.contact_email}
                    </a>
                  ) : <span className="text-zinc-500">—</span>}
                </td>
                <td className="px-4 py-3">
                  {webHref ? (
                    <a href={webHref} target="_blank" rel="noopener noreferrer"
                      data-testid={`supplier-web-${s.id}`}
                      title={s.website}
                      className="inline-flex items-center gap-1 h-8 px-2.5 rounded-sm border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 hover:text-cyan-200 hover:border-cyan-400 uppercase tracking-widest text-[11px] font-semibold transition-colors">
                      <Globe size={12} className="shrink-0" />
                      Web
                    </a>
                  ) : <span className="text-zinc-500">—</span>}
                </td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{s.notes || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" data-testid={`supplier-products-${s.id}`}
                      className="text-cyan-400 hover:text-cyan-300 uppercase tracking-wider text-[11px]"
                      onClick={() => showProducts(s)}>
                      <PackageSearch size={14} className="mr-1" /> Produkte anzeigen
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`supplier-edit-${s.id}`} onClick={() => openEdit(s)}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-rose-400"
                      data-testid={`supplier-delete-${s.id}`} onClick={() => confirmDelete(s)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            );})}
            {!suppliers.data?.length && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Noch keine Lieferanten.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
